import { Autocomplete, Box, Chip, TextField, Tooltip, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useIntl } from "react-intl";
import {
  addOptions,
  addValue,
  allValues,
  chipsFor,
  emptyLinkState,
  hasAnchor,
  isLocked,
  removeValue,
  selectAnchor,
  type FieldKey,
  type LinkState,
  type ReferenceRow,
} from "./qualificationLinkState";
import { displayLinkValue, FIELD_LABEL_ID } from "./wizardTypes";

const FIELDS: FieldKey[] = ["lb", "ab", "sa"];

// An anchor resolves to as many as a hundred values, so a field shows a first row of them and
// keeps the rest behind a "+N" that opens when the field takes focus.
const COLLAPSED_CHIP_LIMIT = 4;

const styles = {
  fieldLabel: { display: "block", mb: 0.75 },
  chips: { display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" },
  // The tags and the input are siblings inside the control, so without a gap the last chip
  // touches the placeholder.
  linkField: { "& .MuiInputBase-root": { gap: 0.75 } },
  lockIcon: { fontSize: 14 },
};

export default function Step1SelectQualification({
  referenceRows,
  state,
  onChange,
}: {
  referenceRows: ReferenceRow[];
  state: LinkState;
  onChange: (next: LinkState) => void;
}) {
  const { formatMessage: t } = useIntl();

  const fieldLabel = (field: FieldKey) => t({ id: FIELD_LABEL_ID[field] });

  const displayValue = (value: string) => displayLinkValue(value, t);

  const anchored = hasAnchor(state);

  // The control reports the whole selection rather than the gesture, and a value can also
  // leave it by Backspace, so the add or remove is derived here. Deriving it is what makes
  // the lock hold: a locked value is refused whichever gesture dropped it.
  const applySelection = (field: FieldKey, next: string[]) => {
    const current = chipsFor(state, field);
    const removed = current.find((value) => !next.includes(value));

    if (removed) {
      if (!isLocked(state, field, removed)) {
        onChange(removeValue(state, field, removed));
      }

      return;
    }

    const added = next.find((value) => !current.includes(value));

    if (added) {
      onChange(addValue(referenceRows, state, field, added));
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t({ id: "skuQualificationWizardStep1Help" })}
      </Typography>

      {FIELDS.map((field) => {
        const isAnchorField = state.anchorField === field;
        const chips = chipsFor(state, field);

        return (
          <Box key={field} sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={styles.fieldLabel}>
              {fieldLabel(field)}
            </Typography>

            {!anchored && (
              // Empty state: free search over all reference values; picking sets the anchor.
              <Autocomplete
                options={allValues(referenceRows, field)}
                value={null}
                onChange={(_, value) =>
                  value && onChange(selectAnchor(referenceRows, field, value))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder={t(
                      { id: "searchFieldPlaceholder" },
                      { field: fieldLabel(field) },
                    )}
                  />
                )}
              />
            )}

            {anchored && isAnchorField && (
              // The anchor resolves to itself, so it has nothing left to offer: a lone chip
              // whose remove clears the whole selection.
              <Box sx={styles.chips}>
                {chips.map((value) => (
                  <Chip
                    key={value}
                    label={displayValue(value)}
                    color="primary"
                    size="small"
                    // Clearing is offered on the chip itself as well as its remove control:
                    // MUI marks a deletable chip up as a button, so a click has to do something.
                    onClick={() => onChange(emptyLinkState())}
                    onDelete={() => onChange(emptyLinkState())}
                  />
                ))}
              </Box>
            )}

            {anchored && !isAnchorField && (
              <Autocomplete
                multiple
                disableClearable
                filterSelectedOptions
                limitTags={COLLAPSED_CHIP_LIMIT}
                sx={styles.linkField}
                // The selected values stay in the options so the control recognises its own
                // value; filterSelectedOptions is what keeps them out of the list box.
                options={[...chips, ...addOptions(referenceRows, state, field)]}
                value={chips}
                onChange={(_, next) => applySelection(field, next)}
                getOptionLabel={displayValue}
                renderTags={(values, getTagProps) =>
                  values.map((value, index) =>
                    isLocked(state, field, value) ? (
                      // Removing it would empty the set, so it carries a lock and no delete.
                      <Tooltip
                        key={value}
                        title={t({ id: "skuQualificationLinkLocked" })}
                      >
                        <Chip
                          label={displayValue(value)}
                          variant="outlined"
                          size="small"
                          icon={<LockIcon sx={styles.lockIcon} />}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        {...getTagProps({ index })}
                        key={value}
                        label={displayValue(value)}
                        variant="outlined"
                        size="small"
                      />
                    ),
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder={t(
                      { id: "addEntity" },
                      { entity: fieldLabel(field) },
                    )}
                  />
                )}
              />
            )}
          </Box>
        );
      })}

      {anchored && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t(
            { id: "skuQualificationEntriesWillBeCreated" },
            { count: state.links.length },
          )}
        </Typography>
      )}
    </Box>
  );
}
